import * as React from "react";
import axios from "axios";
import { MyCard } from "./MyCard";
import { DataTable } from "./ui";

type Examples = {
  hash?: string;
  txid?: string;
  asset?: string;
  height?: number;
};

const FEE = "mgaw88zztsHWN8SyL9vXwiCed7aRiPwL26";

type Row = [string, string, string, string | null];

export function Api() {
  const [examples, setExamples] = React.useState<Examples>({});
  const origin = typeof window !== "undefined" ? window.location.origin : "";

  React.useEffect(() => {
    Promise.all([
      axios.get("/api/blocks").catch(() => ({ data: [] })),
      axios.get("/api/assets").catch(() => ({ data: [] })),
      axios.get("/api/status").catch(() => ({ data: {} })),
    ]).then(([blocks, assets, status]) => {
      const tip = blocks.data?.[0];
      const tx = tip?.tx?.[0];
      setExamples({
        hash: tip?.hash,
        txid: typeof tx === "string" ? tx : tx?.txid,
        asset: assets.data?.[0],
        height: status.data?.blocks ?? tip?.height,
      });
    });
  }, []);

  const path = (p: string) => origin + p;
  const h = examples.hash;
  const tx = examples.txid;
  const asset = examples.asset;
  const height = examples.height;

  const rest: Row[] = [
    ["Meta", "GET /api", "Machine-readable catalog", path("/api")],
    ["Chain", "GET /api/status", "Tip height, difficulty, hashrate, mempool", path("/api/status")],
    ["Chain", "GET /api/blocks/tip/height", "Tip height (Esplora)", path("/api/blocks/tip/height")],
    ["Chain", "GET /api/blocks/tip/hash", "Tip hash (Esplora)", path("/api/blocks/tip/hash")],
    [
      "Chain",
      "GET /api/block-height/:height",
      "Hash at height",
      height != null ? path("/api/block-height/" + height) : null,
    ],
    ["Chain", "GET /api/blocks", "Latest 10 blocks", path("/api/blocks")],
    [
      "Chain",
      "GET /api/blocks/:hash",
      "Block by hash or height",
      h ? path("/api/blocks/" + h) : null,
    ],
    [
      "Chain",
      "GET /api/block/:hash/header",
      "Block header",
      h ? path("/api/block/" + h + "/header") : null,
    ],
    [
      "Chain",
      "GET /api/block/:hash/txids",
      "Txids in the block",
      h ? path("/api/block/" + h + "/txids") : null,
    ],
    [
      "Chain",
      "GET /api/block/:hash/status",
      "Best-chain status",
      h ? path("/api/block/" + h + "/status") : null,
    ],
    ["Chain", "GET /api/supply", "Coin supply from the UTXO set", path("/api/supply")],
    ["Chain", "GET /api/difficulty", "Current difficulty", path("/api/difficulty")],
    ["Chain", "GET /api/networkhashps", "Network hashrate", path("/api/networkhashps")],
    ["Chain", "GET /api/mining", "Mining info", path("/api/mining")],
    ["Chain", "GET /api/network", "Node / network info", path("/api/network")],
    ["Chain", "GET /api/peers", "Connected peers", path("/api/peers")],
    ["Mempool", "GET /api/mempool", "Mempool contents", path("/api/mempool")],
    ["Mempool", "GET /api/mempool/info", "Mempool stats", path("/api/mempool/info")],
    ["Mempool", "GET /api/mempool/txids", "Mempool txid list", path("/api/mempool/txids")],
    ["Mempool", "GET /api/fees/recommended", "Fee estimates (TLS/kvB)", path("/api/fees/recommended")],
    [
      "Transaction",
      "GET /api/tx/:txid",
      "Verbose transaction",
      tx ? path("/api/tx/" + tx) : null,
    ],
    [
      "Transaction",
      "GET /api/tx/:txid/hex",
      "Raw transaction hex",
      tx ? path("/api/tx/" + tx + "/hex") : null,
    ],
    [
      "Transaction",
      "GET /api/tx/:txid/status",
      "Confirmation status",
      tx ? path("/api/tx/" + tx + "/status") : null,
    ],
    ["Transaction", "POST /api/tx", "Broadcast raw hex", null],
    ["Transaction", "POST /api/decode", "Decode raw hex", null],
    ["Assets", "GET /api/assets", "Asset names", path("/api/assets")],
    [
      "Assets",
      "GET /api/assetdata/:name",
      "Asset metadata",
      asset ? path("/api/assetdata/" + encodeURIComponent(asset)) : null,
    ],
    ["Assets", "GET /api/assetcount", "Counts by asset type", path("/api/assetcount")],
    [
      "Address",
      "GET /api/addresses/:address",
      "Balance (needs addressindex)",
      path("/api/addresses/" + FEE),
    ],
    [
      "Address",
      "GET /api/addressdeltas/:address",
      "Full delta dump (prefer /api/addresshistory)",
      path("/api/addressdeltas/" + FEE),
    ],
    [
      "Address",
      "GET /api/addresshistory/:address?limit=10&page=1",
      "Paginated history with from/to counterparties",
      path("/api/addresshistory/" + FEE + "?limit=10&page=1"),
    ],
    [
      "Address",
      "GET /api/getaddressutxos/:address",
      "UTXOs (needs addressindex)",
      path("/api/getaddressutxos/" + FEE),
    ],
    [
      "Address",
      "GET /api/validate-address/:address",
      "validateaddress",
      path("/api/validate-address/" + FEE),
    ],
    ["Search", "GET /gettype/:value", "Classify a hash, address, or asset", path("/gettype/" + (h || "1"))],
  ];

  const aliases: Row[] = [
    ["Cryptoscope", "GET /api/getblockcount", "Height (+ ?legacy=1 plaintext)", path("/api/getblockcount")],
    ["Cryptoscope", "GET /api/getdifficulty", "Difficulty", path("/api/getdifficulty")],
    ["Cryptoscope", "GET /api/getnetworkhashps", "Hashrate", path("/api/getnetworkhashps")],
    ["Cryptoscope", "GET /api/getconnectioncount", "Peer counts", path("/api/getconnectioncount")],
    ["Cryptoscope", "GET /api/getcoinsupply", "Coin supply JSON", path("/api/getcoinsupply")],
    ["Cryptoscope", "GET /api/getmoneysupply", "Coin supply plaintext", path("/api/getmoneysupply")],
    ["Cryptoscope", "GET /api/getassetcount", "Asset counts", path("/api/getassetcount")],
    [
      "Cryptoscope",
      "GET /api/getassetinfo?name=",
      "Asset by name",
      asset ? path("/api/getassetinfo?name=" + encodeURIComponent(asset)) : null,
    ],
    [
      "Cryptoscope",
      "GET /api/getblockhash?index=",
      "Hash by height",
      height != null ? path("/api/getblockhash?index=" + height) : null,
    ],
    [
      "Cryptoscope",
      "GET /api/getblock?hash=",
      "Block by hash",
      h ? path("/api/getblock?hash=" + h) : null,
    ],
    [
      "Cryptoscope",
      "GET /api/getrawtransaction?txid=",
      "Transaction",
      tx ? path("/api/getrawtransaction?txid=" + tx) : null,
    ],
    ["Cryptoscope", "GET /api/getaddnodelist", "Peer addresses", path("/api/getaddnodelist")],
    ["Cryptoscope", "GET /api/getbalance?address=", "Balance (needs addressindex)", path("/api/getbalance?address=" + FEE)],
    ["Cryptoscope", "GET /api/getaddress?address=", "Address + validate", path("/api/getaddress?address=" + FEE)],
  ];

  return (
    <div className="page stack">
      <div className="kicker">HTTP API</div>
      <h1 style={{ fontSize: 28, margin: 0 }}>Connect to this explorer</h1>
      <p className="muted">
        JSON over HTTP, no key. Base URL is this origin. Matches Cryptoscope’s Telestai API where
        the node can answer, plus Esplora/Mempool-style tip, header, hex, fees, validate, and
        broadcast. Address history still needs <span className="mono">addressindex</span> on the
        node. We do not expose wallet clustering, rich lists, markets, or block templates.
      </p>
      <MyCard
        header={"REST · " + origin}
        body={
          <DataTable
            headers={["Group", "Path", "What it returns", "Live"]}
            rows={rest.map(([group, route, desc, href]) => [
              group,
              <span className="mono">{route}</span>,
              desc,
              href ? (
                <a href={href} target="_blank" rel="noreferrer">
                  open
                </a>
              ) : (
                <span className="dim">POST</span>
              ),
            ])}
          />
        }
      />
      <MyCard
        header="Cryptoscope aliases"
        body={
          <DataTable
            headers={["Group", "Path", "What it returns", "Live"]}
            rows={aliases.map(([group, route, desc, href]) => [
              group,
              <span className="mono">{route}</span>,
              desc,
              href ? (
                <a href={href} target="_blank" rel="noreferrer">
                  open
                </a>
              ) : (
                <span className="dim">—</span>
              ),
            ])}
          />
        }
      />
    </div>
  );
}
